/** Contracts & Renewals tab - contract reference/dates/renewal status, sourced from the
 * existing governance profile contract record plus the Legal & Commercial renewal date.
 * Demo Mode only, local-only mock data. */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Chip, Grid, Typography,
} from '@mui/material';
import { Section } from '../../../../../components/ui/Section';
import { getSupplierGovernanceProfile, RenewalStatus } from '../../../../../mocks/data/supplierGovernanceData';
import { getSupplierLegalCommercialInfo } from '../../../../../mocks/data/supplierProfileData';

export interface ContractsRenewalsTabProps {
  supplierId: number;
}

const RENEWAL_STATUS_COLOR: Record<RenewalStatus, 'success' | 'warning' | 'error'> = {
  OnTrack: 'success',
  RenewalDueSoon: 'warning',
  Overdue: 'error',
};

const ContractsRenewalsTab: React.FC<ContractsRenewalsTabProps> = ({ supplierId }) => {
  const { t } = useTranslation('suppliers');
  const governanceProfile = useMemo(() => getSupplierGovernanceProfile(supplierId), [supplierId]);
  const legalCommercialInfo = useMemo(() => getSupplierLegalCommercialInfo(supplierId), [supplierId]);
  const contract = governanceProfile.contract;

  return (
    <Section>
      <Box sx={{ p: 4 }}>
        <Typography variant="textSM"
          fontWeight={600}
          sx={{ mb: 3 }}>{t('contractsRenewals', 'Contracts & renewals')}</Typography>
        {contract ? (
          <Grid container
            spacing={3}>
            <Grid item
              xs={12}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('contractReference', 'Reference')}</Typography>
              <Typography variant="textSM">{contract.contractReference}</Typography>
            </Grid>
            <Grid item
              xs={12}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('contractDates', 'Start / end')}</Typography>
              <Typography variant="textSM">{contract.startDate} - {contract.endDate}</Typography>
            </Grid>
            <Grid item
              xs={12}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('renewalNotice', 'Renewal notice period')}</Typography>
              <Typography variant="textSM">{contract.renewalNoticePeriodDays} {t('days', 'days')}</Typography>
            </Grid>
            <Grid item
              xs={12}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('renewalStatus', 'Renewal status')}</Typography>
              <Chip label={contract.renewalStatus}
                color={RENEWAL_STATUS_COLOR[contract.renewalStatus]}
                size="small" />
            </Grid>
            <Grid item
              xs={12}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('autoRenew', 'Auto-renew')}</Typography>
              <Typography variant="textSM">{contract.autoRenew ? t('yes', 'Yes') : t('no', 'No')}</Typography>
            </Grid>
            <Grid item
              xs={12}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('renewalDate', 'Renewal date')}</Typography>
              <Typography variant="textSM">{legalCommercialInfo.renewalDate || '-'}</Typography>
            </Grid>
          </Grid>
        ) : (
          <Typography variant="textSM">{t('noContract', 'No contract captured yet.')}</Typography>
        )}
      </Box>
    </Section>
  );
};

export default ContractsRenewalsTab;
